import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  SimpleChanges,
  OnChanges,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALUE_ACCESSOR,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { noWhitespaceValidator } from '../../../validators/form-validators';
import { enumToArray } from '@shared/utils/enum-to-array.util';
import { DropdownModel } from '@shared-features/models/dropdown-model';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchInputComponent),
      multi: true,
    },
  ],
})
export class SearchInputComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Output() searchValue = new EventEmitter();
  @Output() clearValue = new EventEmitter();

  @Input() placeholder: string;
  @Input() showSearchActions = true;
  @Input() searchKeyEnum: any;
  @Input() multiple = false;
  @Input() notFoundValues = [];
  @Input() pattern: string | RegExp;
  @Input() iconPosition: 'left' | 'right' = 'left';
  @Input() inputType = 'text';
  @Input() initialValue;
  @Input() clearAllValues;
  @Input() allowSpaceAsSeparator = false;
  @Input() showValueAsTooltip = false;
  @Input() scrollInput = false;
  @Input() searchStyles: { [key: string]: string };
  @Input() ColsNumber = 3;
  @Input() buttonText = 'Buttons.Search';
  @Input() changePlaceholderColor = false;
  @Input() isDisabled = false;
  readonly separatorKeysCodes = [ENTER, COMMA];

  dispose$ = new Subject();
  form: UntypedFormGroup;
  searchKeyItems: DropdownModel[] = [];
  notFoundValuesSet = new Set();
  stringSearchValue: string;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initForm();

    const valueChanges$ = this.multiple
      ? this.form.controls.searchTermList.valueChanges
      : this.form.controls.searchTerm.valueChanges;

    valueChanges$.pipe(debounceTime(500), takeUntil(this.dispose$)).subscribe((value) => {
      if (!this.showSearchActions) {
        this.stringSearchValue = Array.isArray(value) ? value?.join(',') : value;
        this.emitValue();
      }
    });
  }

  private emitValue(): void {
    const searchTerm = this.multiple
      ? this.form.controls.searchTermList.value
      : this.form.controls.searchTerm.value;

    const value = {
      searchTerm: Array.isArray(searchTerm) ? searchTerm.join(',') : searchTerm,
      searchKey: this.form.controls.searchKey.value,
    };

    this.onChange(this.searchKeyEnum ? value : value.searchTerm ? value.searchTerm : null);

    this.searchValue.emit(value.searchTerm ? value.searchTerm : null);
  }

  ngOnChanges({ searchKeyEnum, notFoundValues, clearAllValues }: SimpleChanges): void {
    if (searchKeyEnum && searchKeyEnum.currentValue) {
      this.setupSearchKeyItems();
    }
    if (notFoundValues && notFoundValues.currentValue.length) {
      const notFoundValuesSet = new Set();
      this.notFoundValues.forEach((v) => notFoundValuesSet.add(v.toString()));
      this.notFoundValuesSet = notFoundValuesSet;
    }
    if (clearAllValues && clearAllValues.currentValue) {
      this.onClearButtonClicked();
    }
    this.isDisabled
      ? this.form?.controls?.searchTerm?.disable({ emitEvent: false })
      : this.form?.controls?.searchTerm?.enable({ emitEvent: false });
  }

  ngOnDestroy(): void {
    this.notFoundValuesSet.clear();
    this.dispose$.next(true);
    this.dispose$.complete();
  }

  initForm() {
    this.form = this.fb.group({
      searchTerm: [
        { value: this.multiple ? null : this.initialValue || null, disabled: this.isDisabled },
        [noWhitespaceValidator, Validators.pattern(this.pattern)],
      ],
      searchKey: [this.searchKeyItems[0] || null],
      searchTermList: [this.multiple ? this.initialValue?.split(',') || [] : []],
    });
  }

  writeValue(value: any): void {
    if (value) {
      const searchTerm = this.searchKeyEnum ? value?.searchTerm : value;

      if (this.multiple) {
        this.form.controls.searchTermList.setValue(
          Array.isArray(searchTerm) ? searchTerm : searchTerm?.split(','),
          { emitEvent: false }
        );
      } else {
        this.form.controls.searchTerm.setValue(searchTerm, { emitEvent: false });
      }

      this.form.controls.searchKey.setValue(value?.searchKey, { emitEvent: false });
    } else {
      this.form.patchValue(
        { searchTerm: null, searchKey: null, searchTermList: [] },
        { emitEvent: false }
      );
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  onClearButtonClicked() {
    this.clearValue.emit();
    if (!this.form) return;
    if (this.multiple) {
      this.form.controls.searchTermList.setValue([]);
    } else {
      this.form.controls.searchTerm.setValue(null);
    }

    if (!this.showSearchActions) {
      return;
    }

    if (this.searchKeyItems.length) {
      this.searchValue.emit({
        searchTerm: this.multiple
          ? this.form.controls.searchTermList.value?.join(',')
          : this.form.controls.searchTerm.value,
        searchKey: null,
      });
    } else {
      this.searchValue.emit(
        this.multiple
          ? this.form.controls.searchTermList.value?.join(',')
          : this.form.controls.searchTerm.value
      );
    }
  }

  onSearchButtonClicked() {
    this.notFoundValues = [];

    if (this.searchKeyItems.length) {
      this.searchValue.emit({
        searchTerm: this.multiple
          ? this.form.controls['searchTermList'].value?.join(',')
          : this.form.controls['searchTerm'].value,
        searchKey: this.form.controls['searchKey'].value.dropDownValue,
      });
    } else {
      this.searchValue.emit(
        this.multiple
          ? this.form.controls['searchTermList'].value?.join(',')
          : this.form.controls['searchTerm'].value
      );
    }
  }

  setupSearchKeyItems() {
    this.searchKeyItems = enumToArray(this.searchKeyEnum).map(
      (item: any) =>
        ({
          dropDownDisplayName: item.key,
          dropDownValue: item.value,
        } as DropdownModel)
    );
  }

  removeSearch(searchTerm: string): void {
    this.form.controls.searchTermList.setValue(
      this.form.value.searchTermList?.filter((e) => e !== searchTerm)
    );
  }

  addSearchTerm(event: MatChipInputEvent, input: HTMLInputElement): void {
    const inputValue = event.value.trim();

    const separatorRegex = this.allowSpaceAsSeparator ? /[\s,]+/ : /[,]+/;

    const value = inputValue.split(separatorRegex);

    const valueMap = value.reduce((group, word) => {
      group[word.toLowerCase()] = true;
      return group;
    }, {});

    if (inputValue) {
      this.form.controls.searchTermList.setValue(
        this.form.value.searchTermList.filter((v) => !valueMap[v.toLowerCase()]).concat(value)
      );
      input.value = '';
    }
  }
}
